import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, ChevronUp, ChevronDown, Pencil, Trash2, Download, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ContentEditor from "./ContentEditor";
import { download } from "../url/common";

const missing = (x) => !x.seo_title || !x.meta_description;

export default function ServicesAdmin() {
  const [cats, setCats] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/cms/services").then((r) => setCats(r.data.items));
  useEffect(() => { load(); }, []);
  const move = async (kind, list, i, dir) => {
    const ids = list.map((x) => x.id);
    const j = i + dir; if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/cms/reorder", { kind, ids }); load();
  };
  const del = async (kind, x) => {
    if (!window.confirm(`Delete "${x.name}"?`)) return;
    try { await api.delete(`/admin/cms/${kind}/item/${x.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const togglePub = async (kind, x) => { try { await api.put(`/admin/cms/${kind}/item/${x.id}`, { published: !x.published }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const imp = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/admin/cms/services/import", fd); toast.success(`${r.data.created} created, ${r.data.updated} updated${r.data.errors.length ? `, ${r.data.errors.length} errors` : ""}`); if (r.data.errors.length) toast.error(r.data.errors.slice(0, 3).join(" · ")); load(); }
    catch { toast.error("Import failed"); }
    e.target.value = "";
  };
  const Row = ({ kind, x, list, i, path }) => (
    <div className="flex flex-wrap items-center gap-2 py-2" data-testid={`svc-row-${x.slug}`}>
      <div className="flex flex-col"><button onClick={() => move(kind, list, i, -1)}><ChevronUp size={14} /></button><button onClick={() => move(kind, list, i, 1)}><ChevronDown size={14} /></button></div>
      <div className="min-w-0 flex-1"><div className={`font-semibold ${kind === "service" ? "text-sm" : ""} text-steel`}>{x.name}</div>
        <div className="text-[11px] font-mono text-muted-foreground">{path}{missing(x) && <span className="ml-2 text-amber-700 font-sans">SEO title/description missing</span>}</div></div>
      <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!x.published} onChange={() => togglePub(kind, x)} data-testid={`svc-pub-${x.slug}`} />Published</label>
      {x.published && <a href={path} target="_blank" rel="noreferrer" className="text-steel"><ExternalLink size={15} /></a>}
      <button onClick={() => setEdit({ kind, id: x.id })} data-testid={`svc-edit-${x.slug}`}><Pencil size={15} /></button>
      <button onClick={() => del(kind, x)} className="text-red-600" data-testid={`svc-del-${x.slug}`}><Trash2 size={15} /></button>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="services-admin">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[220px]"><h2 className="text-lg font-extrabold text-steel">Service categories &amp; pages</h2>
          <p className="text-sm text-muted-foreground">Each published category lives at /services/&lt;slug&gt; and each service at /services/&lt;category&gt;/&lt;slug&gt;. The 16 suggested categories start as unpublished templates — add your own text, then publish.</p></div>
        <Button onClick={() => setEdit({ kind: "category" })} className="bg-steel text-white" data-testid="svc-add-category"><Plus size={15} className="mr-1" />Category</Button>
        <Button variant="outline" onClick={() => setEdit({ kind: "service" })} data-testid="svc-add-service"><Plus size={15} className="mr-1" />Service</Button>
        <Button variant="ghost" onClick={() => download("/admin/cms/services/export.csv", "services.csv")} data-testid="svc-export"><Download size={15} className="mr-1" />CSV</Button>
        <label className="inline-flex items-center text-sm cursor-pointer px-3 py-2 rounded-md hover:bg-slate-100" data-testid="svc-import"><Upload size={15} className="mr-1" />Import CSV<input type="file" accept=".csv" className="hidden" onChange={imp} /></label>
      </div>
      <div className="rounded-xl border bg-white divide-y px-3">
        {!cats ? <p className="p-3 text-sm text-muted-foreground">Loading…</p> : cats.map((c, i) => (
          <div key={c.id}>
            <Row kind="category" x={c} list={cats} i={i} path={`/services/${c.slug}`} />
            {c.services.length > 0 && <div className="pl-8 pb-2 divide-y border-l ml-2">{c.services.map((s, j) => <Row key={s.id} kind="service" x={s} list={c.services} i={j} path={`/services/${c.slug}/${s.slug}`} />)}</div>}
          </div>))}
      </div>
      {edit && <ContentEditor kind={edit.kind} id={edit.id} categories={cats || []} services={[]} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
